import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { YouTubePlayerHandle } from './YouTubePlayer.tsx';

/**
 * The host's Spotify player.
 *
 * ## The constraint everything here is shaped by
 *
 * **Spotify's embed will not start a full track from code.** Calling
 * `controller.play()` gets a 30-second preview; only a real press inside the
 * iframe gets the whole song. The iframe is cross-origin, so the parent page
 * cannot hand it a user gesture — there is no trick that fixes this, and the
 * plan's long-open question (§5) is answered: programmatic play is preview-only.
 *
 * Every symptom from the first Spotify game night falls out of one earlier
 * version fighting that:
 *
 * - *"It stops after about five seconds."* We called `play()` — preview — and
 *   then seeked to the submitter's start point, say 1:18. The preview is 30
 *   seconds long, so seeking past its end stopped it dead.
 * - *"I can't scrub."* Thirty seconds to scrub through.
 * - *"Start times don't work."* Same cause.
 * - *"I have to press play as well."* The programmatic call did something, so
 *   the UI said it was playing, and the press that actually mattered looked
 *   like a second one.
 *
 * ## So: cue, ask for one press, then follow
 *
 * Nothing here calls `play()` to begin a track. The track is cued and the stage
 * asks the operator to press ▶ once. From then on this player reports where it
 * is and the server follows — which is the right shape anyway, because in
 * host-only mode this player *is* the audio.
 *
 * A preview announces itself: `playback_update` reports a duration of about 30
 * seconds where the real track is minutes. That is how a preview is told apart
 * from a full track, and it is the only moment worth seeking on — a seek into a
 * preview is what broke it before.
 */

interface SpotifyController {
  loadUri(uri: string): void;
  play(): void;
  pause(): void;
  resume(): void;
  seek(seconds: number): void;
  addListener(event: 'playback_update', cb: (e: { data: PlaybackUpdate }) => void): void;
  addListener(event: 'ready', cb: () => void): void;
  destroy(): void;
}

interface PlaybackUpdate {
  isPaused: boolean;
  isBuffering: boolean;
  position: number;
  duration: number;
}

interface SpotifyIFrameApi {
  createController(
    element: HTMLElement,
    options: { uri: string; width: string | number; height: string | number },
    callback: (controller: SpotifyController) => void,
  ): void;
}

declare global {
  interface Window {
    onSpotifyIframeApiReady?: (api: SpotifyIFrameApi) => void;
  }
}

/**
 * Anything at or under this is a preview, not a track.
 *
 * Spotify's previews are 30 seconds; a little headroom covers rounding and the
 * occasional 29.9. A genuine song shorter than this exists but is vanishingly
 * rare, and treating one as a preview costs only a note on screen.
 */
export const PREVIEW_MAX_MS = 31_000;

let apiPromise: Promise<SpotifyIFrameApi> | null = null;

function loadSpotifyApi(): Promise<SpotifyIFrameApi> {
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<SpotifyIFrameApi>((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error('Spotify player API did not become ready')),
      8_000,
    );
    const previous = window.onSpotifyIframeApiReady;
    window.onSpotifyIframeApiReady = (api) => {
      window.clearTimeout(timeout);
      previous?.(api);
      resolve(api);
    };

    const script = document.createElement('script');
    script.src = 'https://open.spotify.com/embed/iframe-api/v1';
    script.async = true;
    script.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('Spotify player API could not be loaded'));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // A transient blocker or network failure should not poison every later
    // mount for the lifetime of the page.
    apiPromise = null;
    throw error;
  });

  return apiPromise;
}

/**
 * Deliberately the same handle as the YouTube player.
 *
 * `MatchStage` swaps between them by entry source without knowing which it is
 * driving, which is the `EntryPlayer` seam from the plan doing its job. The one
 * difference is honest and unavoidable: `playNow` here cues rather than plays,
 * because it cannot play.
 */
export const SpotifyPlayer = forwardRef<
  YouTubePlayerHandle,
  {
    trackId: string;
    startSeconds: number;
    shouldPlay: boolean;
    awaitingStart: boolean;
    onStarted?: (positionMs: number) => void;
    onPosition?: (positionMs: number, isPlaying: boolean) => void;
    /** Fires when a preview is detected, so the stage can say so. */
    onPreviewDetected?: (isPreview: boolean) => void;
    /** Fires when the embed cannot initialise, so a safe alternative is shown. */
    onUnavailable?: () => void;
    /** A real press inside Spotify can be the single action that starts the room clock. */
    onUserPlay?: () => void;
    /** Album art fills the otherwise empty player area, as a soft backdrop. */
    artworkUrl?: string | null;
  }
>(function SpotifyPlayer(
  { trackId, startSeconds, shouldPlay, awaitingStart, onStarted, onPosition, onPreviewDetected, onUnavailable, onUserPlay, artworkUrl },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<SpotifyController | null>(null);
  const loadedRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // The controller has no getters: position and duration only arrive on the
  // playback_update event, so the last one is kept.
  const lastUpdateRef = useRef<PlaybackUpdate | null>(null);

  /**
   * Where the clip should start, applied once the track proves it is real.
   *
   * Held rather than applied immediately because a seek into a preview is what
   * made playback stop after a few seconds. It is cleared once used, so a
   * manual scrub afterwards is not undone.
   */
  const wantedStartRef = useRef<number | null>(Math.round(startSeconds * 1000));
  const previewReportedRef = useRef<boolean | null>(null);

  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;
  const onPositionRef = useRef(onPosition);
  onPositionRef.current = onPosition;
  const onPreviewRef = useRef(onPreviewDetected);
  onPreviewRef.current = onPreviewDetected;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  const awaitingStartRef = useRef(awaitingStart);
  awaitingStartRef.current = awaitingStart;
  const onUserPlayRef = useRef(onUserPlay);
  onUserPlayRef.current = onUserPlay;
  const userPlayReportedRef = useRef(false);

  const handleUpdate = useCallback((update: PlaybackUpdate) => {
    lastUpdateRef.current = update;

    const playing = !update.isPaused && !update.isBuffering;
    if (playing && !userPlayReportedRef.current) {
      userPlayReportedRef.current = true;
      onUserPlayRef.current?.();
    }

    // Tell the stage which we got, once per verdict rather than per tick.
    if (update.duration > 0) {
      const isPreview = update.duration <= PREVIEW_MAX_MS;
      if (previewReportedRef.current !== isPreview) {
        previewReportedRef.current = isPreview;
        onPreviewRef.current?.(isPreview);
      }

      // A real track, running: now a seek means something. Doing it here rather
      // than at play time is the whole fix for "it stops after five seconds".
      const wanted = wantedStartRef.current;
      if (!isPreview && playing && wanted !== null && wanted > 0) {
        wantedStartRef.current = null;
        controllerRef.current?.seek(wanted / 1000);
        return;
      }
    }

    if (playing && awaitingStartRef.current) {
      onStartedRef.current?.(Math.round(update.position));
      return;
    }

    onPositionRef.current?.(Math.round(update.position), playing);
  }, []);

  useEffect(() => {
    let disposed = false;

    setUnavailable(false);
    void loadSpotifyApi().then((api) => {
      if (disposed || !containerRef.current) return;

      api.createController(
        containerRef.current,
        { uri: `spotify:track:${trackId}`, width: '100%', height: 152 },
        (controller) => {
          if (disposed) {
            controller.destroy();
            return;
          }

          controllerRef.current = controller;
          loadedRef.current = trackId;
          controller.addListener('playback_update', (event) => handleUpdate(event.data));
          setReady(true);
        },
      );
    }).catch(() => {
      if (disposed) return;
      setUnavailable(true);
      onUnavailableRef.current?.();
    });

    return () => {
      disposed = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
      setReady(false);
    };
    // Built once; track changes are handled through the handle below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      /**
       * Cues the track and remembers where it should start. It does **not**
       * play — see the note at the top of this file. The press has to come from
       * the operator, inside the iframe.
       */
      playNow(nextTrackId: string, nextStartSeconds: number) {
        const controller = controllerRef.current;
        wantedStartRef.current = Math.round(nextStartSeconds * 1000);
        previewReportedRef.current = null;
        if (!controller || !ready) return;

        if (loadedRef.current !== nextTrackId) {
          controller.loadUri(`spotify:track:${nextTrackId}`);
          loadedRef.current = nextTrackId;
        }
      },
      resumeNow() {
        // Resuming something already started is allowed: the full track is
        // loaded by then, and this does not fall back to the preview.
        controllerRef.current?.resume();
      },
      pauseNow() {
        controllerRef.current?.pause();
      },
      getPositionMs() {
        return Math.round(lastUpdateRef.current?.position ?? 0);
      },
      getDurationMs() {
        return Math.round(lastUpdateRef.current?.duration ?? 0);
      },
      seekToMs(positionMs: number) {
        const update = lastUpdateRef.current;
        // Refuse to seek a preview. Seeking past its end is what stopped
        // playback dead, and the scrub would be meaningless anyway.
        if (update && update.duration > 0 && update.duration <= PREVIEW_MAX_MS) return;
        controllerRef.current?.seek(positionMs / 1000);
      },
    }),
    [ready],
  );

  useEffect(() => {
    if (!ready || shouldPlay) return;
    controllerRef.current?.pause();
  }, [shouldPlay, ready]);

  // Keep the idle embed showing what is up next.
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !ready || loadedRef.current === trackId) return;

    controller.loadUri(`spotify:track:${trackId}`);
    loadedRef.current = trackId;
    previewReportedRef.current = null;
    wantedStartRef.current = Math.round(startSeconds * 1000);
    userPlayReportedRef.current = false;
  }, [trackId, ready, startSeconds]);

  return (
    <div className="spotify-art-stage relative min-h-[240px] overflow-hidden rounded-xl border border-border bg-black">
      {artworkUrl && <img src={artworkUrl} alt="" aria-hidden className="absolute inset-0 size-full scale-110 object-cover opacity-60 blur-2xl" />}
      <div className="absolute inset-0 bg-black/30" />
      {unavailable ? (
        <div className="relative flex min-h-[240px] flex-col items-center justify-center gap-2 bg-surface-raised/90 px-4 text-center" role="alert">
          <p className="text-sm font-medium">Spotify could not load here.</p>
          <p className="text-xs text-text-muted">A content blocker, network rule, or Spotify outage may be blocking the embed.</p>
          <a className="text-sm text-accent underline" href={`https://open.spotify.com/track/${trackId}`} target="_blank" rel="noreferrer">Open this track in Spotify</a>
        </div>
      ) : (
        <div className="relative flex min-h-[240px] items-center p-4 sm:p-7">
          <div ref={containerRef} className="w-full overflow-hidden rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
});
